import { STYLITICS_CLIENT_IDS } from '../../stylitics'

/**
 * Formats an array of products for Stylitics.
 *
 * @param {object[]} products - An array of product
 * @returns {object[]} An array of formatted item objects for Stylitics integration.
 *
 * @example
 * // Returns:
 * // [
 * //   { id: "97_98416", price: 19.99 },
 * //   { id: "97_98416", price: 19.99 },
 * //   { id: "46_30043", price: 29.99 }
 * // ]
 * const styliticsItems = formatItemsForStylitics([
 *   { sku: "97.98416", price: 19.99, quantity: 2 },
 *   { sku: "46.30043", price: 29.99, quantity: 1 },
 * ]);
 */
export function formatItemsForStylitics(products) {
  return products.flatMap(product => Array(product.quantity).fill(formatItemForStylitics(product)))
}

/**
 * Formats an order product item for Stylitics.
 * @param {{ sku: string; price: number }} product
 * @returns {object} the formatted item object for Stylitics
 */
const formatItemForStylitics = product => {
  return {
    id: getStyliticsItemNumber(product.sku),
    price: product.price,
  }
}

/**
 * Formats the product sku for the Stylitics integration.
 * The Stylitics item number corresponds to our SKU,
 * but with underscores (_) instead of dots (.)
 * @param {string} product sku
 * @returns {string} the Stylitics item number
 * @example
 * // Returns: "97_98416"
 * const styliticsItemNumber = getStyliticsItemNumber("97.98416");
 */
const getStyliticsItemNumber = productSku => productSku.replace(/\./g, '_')

/**
 * Converts a locale formatted as "en-de" or "en-gb" to a BCP 47 language tag format like "en-DE" or "en-GB".
 *
 * @param {string} locale - The locale to convert.
 * @returns {string} The converted BCP 47 language tag
 */
export const normalizeLocale = locale => {
  const [language, country] = locale.split('-')
  return `${language.toLowerCase()}-${country.toUpperCase()}`
}

/**
 * Retrieves the client ID associated with a specific country.
 *
 * @param {string} country - The country code.
 * @returns {string | undefined} The client ID for the given country, or undefined if not found.
 */
export const getClientIdByCountry = country => {
  return STYLITICS_CLIENT_IDS[country]
}
